import React, { useEffect, useState } from 'react';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import BrandMark from '../components/BrandMark';
import SectionHead from '../components/SectionHead';
import useDocumentMeta from '../hooks/useDocumentMeta';
import useReveal from '../hooks/useReveal';

const COLORS = [
  { name: 'Porcelain', hex: '#F6F5F1', role: 'Page background', ink: 'dark' },
  { name: 'Bone', hex: '#EEEDE8', role: 'Sunken panels', ink: 'dark' },
  { name: 'White', hex: '#FFFFFF', role: 'Raised cards', ink: 'dark' },
  { name: 'Obsidian', hex: '#11151B', role: 'Covers and primary text', ink: 'light' },
  { name: 'Graphite', hex: '#252A34', role: 'Secondary text', ink: 'light' },
  { name: 'Slate', hex: '#777D8B', role: 'Labels', ink: 'light' },
  { name: 'Violet', hex: '#7154D8', role: 'Accent on light grounds', ink: 'light' },
  { name: 'Iris', hex: '#8B7DFF', role: 'Accent on dark grounds', ink: 'dark' },
  { name: 'Lilac', hex: '#C9BDF5', role: 'Soft fills', ink: 'dark' },
];

function Hex({ value }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <button className="brand-hex" type="button" onClick={copy} aria-label={`Copy ${value}`}>
      <span aria-live="polite">{copied ? 'Copied' : value}</span>
    </button>
  );
}

function Lockup({ className = '' }) {
  return (
    <span className={`brand-lockup ${className}`.trim()}>
      <BrandMark className="brand-lockup__mark" />
      <span className="brand-lockup__name">QSI Labs</span>
    </span>
  );
}

export default function BrandPage() {
  useReveal();
  useDocumentMeta({
    title: 'Brand | QSI Labs',
    description: 'QSI Labs brand colors, logos and the rules for using the name and the mark.',
    path: '/brand/',
  });

  return (
    <>
      <a className="skip" href="#main">Skip to content</a>
      <Nav variant="brand" />
      <main id="main">
        <header className="brand-hero theme-dark grain" data-nav-tone="dark">
          <div className="container">
            <p className="eyebrow">QSI Labs</p>
            <h1 className="display brand-hero__title">Brand</h1>
            <p className="deck">Colors, the mark, and the rules for using them.</p>
          </div>
        </header>

        <section className="section" id="colors" data-nav-tone="light">
          <div className="container">
            <SectionHead
              index="01"
              eyebrow="Colors"
              title="Obsidian, porcelain, one violet."
              lede="Nine colors. Obsidian and Porcelain carry the page. Violet is the only accent on a light ground; Iris takes that role on a dark one."
            />
            <ul className="brand-swatches">
              {COLORS.map(({ name, hex, role, ink }) => (
                <li key={hex} className={`brand-swatch brand-swatch--${ink}`} style={{ background: hex }}>
                  <span className="brand-swatch__name">{name}</span>
                  <span className="brand-swatch__role">{role}</span>
                  <Hex value={hex} />
                </li>
              ))}
            </ul>
            <p className="brand-note">
              Headings are Geist, weight 600. Decks and quotations are Newsreader. Labels are Geist Mono.
            </p>
          </div>
        </section>

        <section className="section" id="logos" data-nav-tone="light">
          <div className="container">
            <SectionHead
              index="02"
              eyebrow="Logos"
              title="A ring and a core."
              lede="The mark stands alone, or beside the name. Use Obsidian on Porcelain, Bone or White. Use Porcelain on Obsidian."
            />
            <div className="brand-assets">
              <figure className="brand-asset">
                <div className="brand-plates">
                  <div className="brand-plate" aria-hidden="true">
                    <BrandMark className="brand-plate__mark" />
                  </div>
                  <div className="brand-plate brand-plate--ink" aria-hidden="true">
                    <BrandMark className="brand-plate__mark" />
                  </div>
                </div>
                <figcaption>
                  <h3>Mark</h3>
                  <p>The ring is open. The core is a filled point at the center. Leave the inside of the ring empty.</p>
                  <p className="brand-downloads">
                    <a className="btn btn--small btn--ghost" href="/brand/qsi-mark.svg" download>Obsidian SVG</a>
                    <a className="btn btn--small btn--ghost" href="/brand/qsi-mark-porcelain.svg" download>Porcelain SVG</a>
                  </p>
                </figcaption>
              </figure>

              <figure className="brand-asset">
                <div className="brand-plates">
                  <div className="brand-plate" aria-hidden="true">
                    <Lockup />
                  </div>
                  <div className="brand-plate brand-plate--ink" aria-hidden="true">
                    <Lockup />
                  </div>
                </div>
                <figcaption>
                  <h3>Lockup</h3>
                  <p>The name is QSI Labs, set in Geist SemiBold, with a space. The mark sits to the left of the wordmark.</p>
                  <p className="brand-downloads">
                    <a className="btn btn--small btn--ghost" href="/brand/qsi-lockup.svg" download>Obsidian SVG</a>
                    <a className="btn btn--small btn--ghost" href="/brand/qsi-lockup-porcelain.svg" download>Porcelain SVG</a>
                  </p>
                </figcaption>
              </figure>

              <figure className="brand-asset">
                <div className="brand-plates">
                  <div className="brand-plate brand-plate--icon">
                    <img src="/favicon.svg" alt="QSI Labs favicon, porcelain mark on an obsidian square" width="64" height="64" />
                  </div>
                </div>
                <figcaption>
                  <h3>Favicon</h3>
                  <p>The mark on a filled Obsidian square. This is the only approved place for that square.</p>
                  <p className="brand-downloads">
                    <a className="btn btn--small btn--ghost" href="/favicon.svg" download>Favicon SVG</a>
                  </p>
                </figcaption>
              </figure>
            </div>
          </div>
        </section>

        <section className="section" id="usage" data-nav-tone="light">
          <div className="container">
            <SectionHead
              index="03"
              eyebrow="Usage"
              title="Identify us. Leave the mark alone."
              lede="The name, the mark and the lockup identify QSI Labs. These rules are the permission to use them."
            />
            <div className="brand-rules">
              <article>
                <h3>You may</h3>
                <ul>
                  <li>Use the mark and the lockup to identify QSI Labs, or to link to this site, in editorial writing, citations and news.</li>
                  <li>Use the SVG files on this page, unchanged.</li>
                  <li>Set the mark in Obsidian on Porcelain, Bone or White, and in Porcelain on Obsidian.</li>
                </ul>
              </article>
              <article>
                <h3>Do not</h3>
                <ul>
                  <li>Recolor, fill, rotate, outline, shadow or stretch the mark. The ring stays empty.</li>
                  <li>Set the wordmark in another typeface, or remove the space in QSI Labs.</li>
                  <li>Use the marks on merchandise, in a product name, in an advertisement, or in a way that says QSI Labs made, sponsors or endorses something it did not.</li>
                  <li>Register a confusingly similar name or mark.</li>
                  <li>Treat marks of other companies that appear in QSI Labs research as ours. Those stay with their owners.</li>
                </ul>
              </article>
              <article>
                <h3>Clear space and size</h3>
                <ul>
                  <li>Leave empty space around the mark at least as wide as the core, on every side.</li>
                  <li>On screen the mark is at least 16 pixels tall. In print it is at least 5 mm tall.</li>
                  <li>The lockup wordmark is at least 14 pixels tall.</li>
                </ul>
              </article>
              <article>
                <h3>The name</h3>
                <ul>
                  <li>The written name is QSI Labs.</li>
                  <li>The long form is Quantitative Super Intelligence Labs.</li>
                  <li>QSILabs, without a space, is for the domain and for file names.</li>
                </ul>
              </article>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
